import React, { useCallback, useEffect, useState } from 'react';
import {
  apiAskDoubt, apiRateFeedback, apiStudentTasks, apiSubmitTask, apiTaskFileUrl, apiUploadTaskFile,
} from '../lib/studentApi';
import type { MyTask, MyTaskFile, MyTaskSummary } from '../lib/studentApi';

// ── My Tasks (the student portal) ────────────────────────────
// The tasks your mentor gave you: send a link and/or files with a note (you
// can change it until it is reviewed; after the due date it still goes,
// marked late). Your mentor reviews it within 48 hours: a score out of 10,
// what's good, what to improve, and Accepted or Redo (fix it and send again).
// Ask a doubt under the feedback. Mirrors the app's student_tasks_screen.dart.

const IST = 'Asia/Kolkata';
const when = (iso: string) => new Date(iso).toLocaleString('en-IN', { timeZone: IST, weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
const MAX_FILES = 3;
const MAX_BYTES = 10 * 1024 * 1024;
const card = { background: '#fff', border: '1px solid #f1f5f9', boxShadow: '0 2px 10px rgba(0,0,0,.04)' };
const inputStyle = { background: '#f8fafc', border: '1.5px solid #e2e8f0' };

type Filter = 'todo' | 'waiting' | 'done';

function statusOf(t: MyTask, today: string): { text: string; bg: string; fg: string } {
  if (t.status === 'Accepted') return { text: `✅ ${t.score}/10 · Accepted`, bg: '#dcfce7', fg: '#166534' };
  if (t.status === 'Submitted') return { text: 'Sent · waiting for review', bg: '#ede9fe', fg: '#5b21b6' };
  if (t.status === 'Redo') return { text: `🔁 Redo · ${t.score}/10`, bg: '#fef3c7', fg: '#92400e' };
  if (t.overdue) return { text: 'Overdue', bg: '#fee2e2', fg: '#b91c1c' };
  if (t.dueDate === today) return { text: 'Due today', bg: '#fef3c7', fg: '#92400e' };
  return { text: `Due ${t.dueLabel}`, bg: '#f1f5f9', fg: '#475569' };
}

// Send (or change, or send again): a link and/or up to 3 files, and a note
const SendForm: React.FC<{ token: string; task: MyTask; onDone: (t: MyTask) => void; onCancel: () => void }> = ({ token, task, onDone, onCancel }) => {
  const last = task.status === 'Submitted' ? task.tries[task.tries.length - 1] : null;
  const [link, setLink] = useState(last?.link || '');
  const [note, setNote] = useState(last?.note || '');
  const [files, setFiles] = useState<MyTaskFile[]>(last?.files || []);
  const [uploading, setUploading] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const add = async (list: FileList | null) => {
    setError('');
    for (const f of Array.from(list || [])) {
      if (files.length >= MAX_FILES) { setError(`At most ${MAX_FILES} files. Share a link for more.`); break; }
      if (f.size > MAX_BYTES) { setError(`${f.name} is over 10 MB. Share a link (Drive, GitHub) instead.`); continue; }
      setUploading(f.name);
      try { const up = await apiUploadTaskFile(token, task.id, f); setFiles(prev => [...prev, up]); }
      catch (e: any) { setError(e.message); }
    }
    setUploading('');
  };
  const send = async () => {
    setError('');
    if (!link.trim() && !files.length) { setError('Add a link or a file'); return; }
    if (link.trim() && !/^https?:\/\//i.test(link.trim())) { setError('The link should start with https://'); return; }
    setBusy(true);
    try { onDone((await apiSubmitTask(token, task.id, { link: link.trim(), note: note.trim(), files })).task); }
    catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl p-3 space-y-3" style={{ background: '#fffbeb', border: '1.5px solid #fde68a' }}>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wide mb-1" style={{ color: '#64748b' }}>Link (GitHub, Drive, Colab, Power BI…)</label>
        <input value={link} onChange={e => setLink(e.target.value)} placeholder="https://…" className="w-full px-3 py-2.5 rounded-xl text-sm outline-none" style={inputStyle} />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wide mb-1" style={{ color: '#64748b' }}>Files (up to 3, 10 MB each)</label>
        {files.map(f => (
          <div key={f.path} className="flex items-center gap-2 text-sm py-1">
            <span className="flex-1 truncate">📎 {f.name}</span>
            <button onClick={() => setFiles(files.filter(x => x.path !== f.path))} className="text-xs font-bold text-red-600">Remove</button>
          </div>
        ))}
        {files.length < MAX_FILES && (
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold cursor-pointer" style={{ background: '#fff', border: '1.5px dashed #cbd5e1', color: '#475569' }}>
            {uploading ? `Uploading ${uploading}…` : '＋ Add a file'}
            <input type="file" multiple className="hidden" disabled={!!uploading} onChange={e => { add(e.target.files); e.target.value = ''; }} />
          </label>
        )}
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wide mb-1" style={{ color: '#64748b' }}>Note to your mentor (optional)</label>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={1000} placeholder="Where you got stuck, what you tried…" className="w-full px-3 py-2.5 rounded-xl text-sm outline-none resize-none" style={inputStyle} />
      </div>
      {error && <p className="text-xs font-semibold" style={{ color: '#b91c1c' }}>{error}</p>}
      <div className="flex gap-2">
        <button onClick={send} disabled={busy || !!uploading} className="flex-1 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50" style={{ background: 'linear-gradient(135deg,#2b2240,#3d2d58)', color: '#fff' }}>
          {busy ? 'Sending…' : task.status === 'Submitted' ? 'Save changes' : task.status === 'Redo' ? 'Send again' : 'Send to my mentor'}
        </button>
        <button onClick={onCancel} className="px-4 rounded-xl text-sm font-bold" style={{ background: '#f1f5f9', color: '#64748b' }}>Cancel</button>
      </div>
    </div>
  );
};

const TaskCard: React.FC<{ token: string; task: MyTask; today: string; onChanged: (t: MyTask) => void }> = ({ token, task: t, today, onChanged }) => {
  const [sending, setSending] = useState(false);
  // Open (with its Send button) until it is accepted; a done one opens on a tap
  const [open, setOpen] = useState(t.status !== 'Accepted');
  const [doubt, setDoubt] = useState('');
  const [busy, setBusy] = useState(false);
  const s = statusOf(t, today);
  const review = t.reviews[t.reviews.length - 1];
  const last = t.tries[t.tries.length - 1];
  const openFile = async (path: string) => {
    try { window.open(await apiTaskFileUrl(token, t.id, path), '_blank', 'noopener'); } catch (e: any) { alert(e.message); }
  };
  const ask = async () => {
    if (!doubt.trim()) return;
    setBusy(true);
    try { onChanged((await apiAskDoubt(token, t.id, doubt.trim())).task); setDoubt(''); } catch (e: any) { alert(e.message); } finally { setBusy(false); }
  };
  const rate = async (helpful: 'yes' | 'no') => {
    try { onChanged((await apiRateFeedback(token, t.id, helpful)).task); } catch (e: any) { alert(e.message); }
  };
  return (
    <div className="rounded-2xl overflow-hidden" style={card}>
      <button onClick={() => setOpen(o => !o)} className="w-full text-left px-4 py-3.5 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm" style={{ color: '#0f172a' }}>{t.title}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">From {t.givenByName} · due {t.dueLabel}{t.tries.length > 1 ? ` · ${t.tries.length} tries` : ''}</p>
        </div>
        <span className="text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap" style={{ background: s.bg, color: s.fg }}>{s.text}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {t.details && <p className="text-sm whitespace-pre-wrap rounded-xl px-3 py-2" style={{ background: '#f8fafc', color: '#334155' }}>{t.details}</p>}
          {review && (
            <div className="rounded-xl p-3 space-y-1.5" style={{ background: review.result === 'Accepted' ? '#f0fdf4' : '#fffbeb' }}>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-black" style={{ color: review.result === 'Accepted' ? '#166534' : '#92400e' }}>{review.score}/10</span>
                <span className="text-xs font-bold" style={{ color: '#475569' }}>{review.result === 'Accepted' ? 'Accepted' : 'Redo: fix it and send again'} · {review.byName}</span>
              </div>
              <p className="text-sm" style={{ color: '#14532d' }}><b>👍 Good:</b> {review.good}</p>
              <p className="text-sm" style={{ color: '#78350f' }}><b>🔧 Improve:</b> {review.improve}</p>
              <div className="flex items-center gap-2 pt-1">
                <span className="text-[11px] text-slate-500">Was this feedback helpful?</span>
                {(['yes', 'no'] as const).map(h => (
                  <button key={h} onClick={() => rate(h)} className="text-sm px-2 py-0.5 rounded-lg" aria-pressed={review.helpful === h}
                    style={{ background: review.helpful === h ? (h === 'yes' ? '#bbf7d0' : '#fecaca') : '#fff', border: '1px solid #e2e8f0' }}>{h === 'yes' ? '👍' : '👎'}</button>
                ))}
              </div>
            </div>
          )}
          {last && (
            <div className="text-xs space-y-1" style={{ color: '#475569' }}>
              <p className="font-bold">Your work{t.tries.length > 1 ? ` (try ${last.n})` : ''} · sent {when(last.at)}{last.late ? ' · late' : ''}</p>
              {last.link && <a href={last.link} target="_blank" rel="noreferrer" className="block underline break-all" style={{ color: '#6d28d9' }}>🔗 {last.link}</a>}
              {last.files.map(f => <button key={f.path} onClick={() => openFile(f.path)} className="block underline text-left" style={{ color: '#6d28d9' }}>📎 {f.name}</button>)}
              {last.note && <p>“{last.note}”</p>}
            </div>
          )}
          {t.doubts.length > 0 && (
            <div className="space-y-1.5">
              {t.doubts.map((d, i) => (
                <div key={i} className={`text-sm rounded-xl px-3 py-2 ${d.from === 'student' ? 'ml-8' : 'mr-8'}`} style={{ background: d.from === 'student' ? '#eff6ff' : '#f5f3ff', color: '#1e293b' }}>
                  <p className="text-[10px] font-bold text-slate-400">{d.from === 'student' ? 'You' : d.byName} · {when(d.at)}</p>
                  <p>{d.text}</p>
                </div>
              ))}
            </div>
          )}
          {sending ? (
            <SendForm token={token} task={t} onCancel={() => setSending(false)} onDone={n => { setSending(false); onChanged(n); }} />
          ) : t.status !== 'Accepted' && (
            <button onClick={() => setSending(true)} className="w-full py-2.5 rounded-xl text-sm font-bold"
              style={t.status === 'Submitted' ? { background: '#f1f5f9', color: '#475569' } : { background: 'linear-gradient(135deg,#F0B429,#FF6535)', color: '#fff' }}>
              {t.status === 'Submitted' ? 'Change what I sent' : t.status === 'Redo' ? 'Send it again' : 'Send my work'}
            </button>
          )}
          {(review || t.doubts.length > 0) && (
            <div className="flex gap-2">
              <input value={doubt} onChange={e => setDoubt(e.target.value)} placeholder="Ask your mentor a doubt…" className="flex-1 px-3 py-2 rounded-xl text-sm outline-none" style={inputStyle} />
              <button onClick={ask} disabled={busy || !doubt.trim()} className="px-4 rounded-xl text-xs font-bold disabled:opacity-50" style={{ background: '#2b2240', color: '#fff' }}>Ask</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function MyTasksTab({ token, onSignOut }: { token?: string; onSignOut: () => void }) {
  const [tasks, setTasks] = useState<MyTask[] | null>(null);
  const [summary, setSummary] = useState<MyTaskSummary | null>(null);
  const [today, setToday] = useState('');
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<Filter>('todo');
  const load = useCallback(async () => {
    if (!token) return;
    try { const r = await apiStudentTasks(token); setTasks(r.tasks); setSummary(r.summary); setToday(r.today); setError(''); }
    catch (e: any) { setError(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (!token || /sign in again/i.test(error)) {
    return (
      <div className="px-4 pt-10 pb-10 text-center space-y-3">
        <p className="text-4xl">🔐</p>
        <p className="font-bold" style={{ color: '#0f172a' }}>Sign in again to see your tasks</p>
        <p className="text-sm text-slate-500">My Tasks is new. Sign out and sign in once more.</p>
        <button onClick={onSignOut} className="px-5 py-2.5 rounded-xl text-sm font-bold" style={{ background: '#2b2240', color: '#fff' }}>Sign out</button>
      </div>
    );
  }
  if (error) return <p className="px-4 pt-10 text-center text-sm" style={{ color: '#b91c1c' }}>{error}</p>;
  if (!tasks || !summary) return <p className="px-4 pt-10 text-center text-sm text-slate-400">Loading your tasks…</p>;

  const lists: Record<Filter, MyTask[]> = {
    todo: tasks.filter(t => t.status === 'Given' || t.status === 'Redo'),
    waiting: tasks.filter(t => t.status === 'Submitted'),
    done: tasks.filter(t => t.status === 'Accepted').reverse(),
  };
  const changed = (n: MyTask) => { setTasks(prev => (prev || []).map(t => (t.id === n.id ? n : t))); load(); };
  return (
    <div className="px-4 pt-6 pb-10 space-y-5">
      <div className="grid grid-cols-4 gap-2">
        {[
          ['Average', summary.avgScore === null ? '—' : `${summary.avgScore}/10`],
          ['On time', summary.onTimePct === null ? '—' : `${summary.onTimePct}%`],
          ['To send', `${summary.toSend}`],
          ['Done', `${summary.done}/${summary.given}`],
        ].map(([k, v]) => (
          <div key={k} className="rounded-2xl px-2 py-3 text-center" style={card}>
            <p className="text-lg font-black" style={{ color: k === 'To send' && summary.overdue ? '#b91c1c' : '#0f172a' }}>{v}</p>
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{k}</p>
          </div>
        ))}
      </div>
      {summary.overdue > 0 && <p className="text-sm font-semibold rounded-xl px-4 py-2.5" style={{ background: '#fee2e2', color: '#b91c1c' }}>⏰ {summary.overdue} task{summary.overdue === 1 ? ' is' : 's are'} overdue — send {summary.overdue === 1 ? 'it' : 'them'} today.</p>}
      <div className="flex gap-2">
        {([['todo', 'To do'], ['waiting', 'Waiting'], ['done', 'Done']] as [Filter, string][]).map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} aria-pressed={filter === k} className="flex-1 py-2 rounded-xl text-xs font-bold"
            style={filter === k ? { background: '#2b2240', color: '#fff' } : { background: '#fff', color: '#64748b', border: '1px solid #e2e8f0' }}>
            {label} {lists[k].length}
          </button>
        ))}
      </div>
      {!lists[filter].length ? (
        <div className="rounded-2xl p-8 text-center" style={card}>
          <p className="text-3xl">{filter === 'todo' ? '🎉' : '📭'}</p>
          <p className="text-sm font-bold mt-2" style={{ color: '#0f172a' }}>{filter === 'todo' ? 'Nothing to send right now' : filter === 'waiting' ? 'Nothing waiting for review' : 'No accepted tasks yet'}</p>
          <p className="text-xs text-slate-400 mt-1">Your mentor gives tasks in class; they show up here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {lists[filter].map(t => <TaskCard key={`${t.id}-${t.status}-${t.tries.length}`} token={token} task={t} today={today} onChanged={changed} />)}
        </div>
      )}
    </div>
  );
}
